import type { AdminAgentChatControlsProps } from "../models/admin-agent.models";
import { ADMIN_AGENT_STATUS_LABELS, ADMIN_AGENT_TITLE_MAX_LENGTH } from "../utils/admin-agent.constants";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { type FormEvent, useEffect, useState } from "react";

// Manage an accessible chat separately from the message composer and running task.
const AdminAgentChatControls = ({
  conversations,
  selectedId,
  conversation,
  busy,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}: AdminAgentChatControlsProps) => {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    setRenaming(false);
  }, [selectedId]);

  const startRenaming = () => {
    setTitle(conversation?.title ?? "");
    setRenaming(true);
  };

  const saveTitle = async (event: FormEvent) => {
    event.preventDefault();

    if (title.trim() && (await onRename(title))) {
      setRenaming(false);
    }
  };

  const confirmDeletion = () => {
    if (window.confirm("Delete this chat and its history? Any running work in this chat will be stopped.")) {
      void onDelete();
    }
  };

  return (
    <div
      data-test="admin-agent-chat-controls"
      className="border-greyscale-20 shrink-0 border-b px-3 py-1.5 dark:border-gray-700"
    >
      <label htmlFor="admin-agent-conversation" className="sr-only">
        Chats
      </label>
      <div className="flex items-center gap-1">
        <select
          id="admin-agent-conversation"
          data-test="admin-agent-chat-select"
          value={selectedId ?? ""}
          disabled={busy || conversations.length === 0}
          onChange={(event) => onSelect(event.target.value)}
          className="h-8 min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 text-xs disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="" disabled>
            {conversations.length ? "Select a chat" : "No chats yet"}
          </option>
          {conversations.map((chat) => (
            <option key={chat.id} value={chat.id}>
              {chat.title}
              {chat.status === "running" ? ` · ${ADMIN_AGENT_STATUS_LABELS[chat.status]}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-test="admin-agent-chat-create"
          aria-label="New chat"
          title="New chat"
          disabled={busy}
          onClick={() => void onCreate()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-green text-white hover:bg-[#166b60] disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        {conversation && !renaming && (
          <>
            <button
              type="button"
              data-test="admin-agent-chat-rename"
              aria-label="Rename chat"
              title="Rename chat"
              disabled={busy}
              onClick={startRenaming}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              data-test="admin-agent-chat-delete"
              aria-label="Delete chat"
              title="Delete chat"
              disabled={busy}
              onClick={confirmDeletion}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {conversation && renaming && (
        <form onSubmit={saveTitle} className="mt-1.5 flex flex-wrap gap-2" data-test="admin-agent-chat-rename-form">
          <label htmlFor="admin-agent-chat-title" className="sr-only">
            New chat title
          </label>
          <input
            id="admin-agent-chat-title"
            data-test="admin-agent-chat-title"
            value={title}
            autoFocus
            maxLength={ADMIN_AGENT_TITLE_MAX_LENGTH}
            disabled={busy}
            onChange={(event) => setTitle(event.target.value)}
            className="h-8 min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
          <button
            type="submit"
            data-test="admin-agent-chat-save-title"
            disabled={busy || !title.trim()}
            className="rounded bg-green px-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            data-test="admin-agent-chat-cancel-rename"
            disabled={busy}
            onClick={() => setRenaming(false)}
            className="px-1 text-xs underline disabled:opacity-50"
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
};

export default AdminAgentChatControls;
